import { useTranslation } from 'react-i18next';

import Table from '../../../../../../../shared/components/Globals/Table/Table';
import CustomModal from '../../../../../../../shared/components/Modals/CustomModal/CustomModal';
import type { ICommercialAddon, IPriceStructureTier } from '../../../../../../../shared/interfaces/quotes';
import { TRANSLATIONS_QUOTES } from '../../../../../../../shared/translations/quotes';
import { formatCurrency } from '../../../../../../../shared/utils/currency/currency';
import { addonRequiresTobacco } from '../../../../../../../shared/utils/quotes/quotes';
import './RateTableModal.scss';

interface IRateTableModalProps {
  groupName?: string;
  addon: ICommercialAddon;
  onClose: () => void;
}

const ageLabel = (from: number, to: number): string => (to >= 99 ? `${from}+` : `${from}-${to}`);
const cell = (value?: number): string => (value == null ? '—' : formatCurrency(value));

const RateTableModal = ({ groupName, addon, onClose }: IRateTableModalProps) => {
  const { t } = useTranslation();
  const allTiers = addon.priceStructure?.structure ?? [];
  const splitByTobacco = addonRequiresTobacco(addon);

  const hasTitleContent = Boolean(groupName || addon.description);

  const title = hasTitleContent ? (
    <div>
      {groupName && <span>{groupName}</span>}
      {addon.description && <div className="rate-table__subtitle">{addon.description}</div>}
    </div>
  ) : undefined;

  const renderTable = (tiers: IPriceStructureTier[], heading?: string) => (
    <>
      {heading && <div className="rate-table__group-title">{heading}</div>}
      <div className="rate-table__scroll">
        <Table density="sm">
          <Table.Head>
            <Table.Row>
              <Table.HeaderCell scope="col">{t(TRANSLATIONS_QUOTES.create.rateTable.age)}</Table.HeaderCell>
              <Table.HeaderCell scope="col">{t(TRANSLATIONS_QUOTES.create.rateTable.individual)}</Table.HeaderCell>
              <Table.HeaderCell scope="col">{t(TRANSLATIONS_QUOTES.create.rateTable.partner)}</Table.HeaderCell>
              <Table.HeaderCell scope="col">{t(TRANSLATIONS_QUOTES.create.rateTable.family)}</Table.HeaderCell>
              <Table.HeaderCell scope="col">{t(TRANSLATIONS_QUOTES.create.rateTable.partnerFamily)}</Table.HeaderCell>
            </Table.Row>
          </Table.Head>
          <Table.Body>
            {tiers.map((tier) => (
              <Table.Row key={`${tier.age.from}-${tier.age.to}`}>
                <Table.Cell>{ageLabel(tier.age.from, tier.age.to)}</Table.Cell>
                <Table.Cell>{cell(tier.base_price)}</Table.Cell>
                <Table.Cell>{cell(tier.price_partner)}</Table.Cell>
                <Table.Cell>{cell(tier.price_family)}</Table.Cell>
                <Table.Cell>{cell(tier.price_partner_and_family)}</Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      </div>
    </>
  );

  return (
    <CustomModal open onClose={onClose} onBackdropClick={onClose} size="xl" title={title}>
      <div className="rate-table">
        <div className="rate-table__name">{addon.name}</div>
        {splitByTobacco ? (
          <>
            {renderTable(
              allTiers.filter((tier) => tier.other_factors?.tobacco !== true),
              t(TRANSLATIONS_QUOTES.create.tobacco.nonTobacco)
            )}
            {renderTable(
              allTiers.filter((tier) => tier.other_factors?.tobacco === true),
              t(TRANSLATIONS_QUOTES.create.tobacco.tobacco)
            )}
          </>
        ) : (
          renderTable(allTiers)
        )}
      </div>
    </CustomModal>
  );
};

export default RateTableModal;
